import { useCallback, useState } from 'react';
import type { UseModalReturn } from '../types';

/**
 * Hook personalizado para manejar el estado y acciones de un modal
 *
 * Proporciona funcionalidad para abrir/cerrar modales con callback opcional.
 * Optimizado con useCallback para evitar re-renders innecesarios.
 *
 * @param onCloseModal - Callback opcional ejecutado al cerrar el modal
 * @returns Objeto con estado y funciones para controlar el modal
 *
 * @example
 * const { isOpen, openModal, closeModal } = useModal(() => {
 *   console.log('Modal cerrado');
 * });
 */
export const useModal = (onCloseModal?: () => void): UseModalReturn => {
  // ============================================================================
  // ESTADO LOCAL
  // ============================================================================

  const [isOpen, setIsOpen] = useState(false);

  // ============================================================================
  // FUNCIONES MEMOIZADAS
  // ============================================================================

  /**
   * Abre el modal
   */
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  /**
   * Cierra el modal y ejecuta callback opcional.
   *
   * `onCloseModal` corre en un macrotask (`setTimeout(0)`) porque el
   * `FocusScope` `trapped` de Radix sigue interceptando `focus()` de forma
   * síncrona hasta que el `Dialog` termina de desmontarse; llamarlo antes
   * hace que cualquier `focus()` manual del consumidor sea redirigido de
   * vuelta al modal en vez de aplicarse (ver SAAS-3762).
   */
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => onCloseModal?.(), 0);
  }, [onCloseModal]);

  // ============================================================================
  // RETORNO DEL HOOK
  // ============================================================================

  return {
    isOpen,
    openModal,
    closeModal,
  };
};
